import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { setCurrentUserId } from "@/stores/dbSync";
import { initAnalytics } from "@/lib/analytics";
import { CookieConsentBanner } from "@/components/shared/CookieConsentBanner";
import { supabase } from "@/integrations/supabase/client";
import * as agentChatService from "@/stores/agentChatService";
import { syncTradesFromDB } from "@/stores/tradeStore";
import { syncViolationsFromDB } from "@/stores/violationStore";
import { useLegalAcceptance } from "@/hooks/useLegalAcceptance";
import { LegalAcceptanceModal } from "@/components/legal/LegalAcceptanceModal";
import { DashboardSkeleton, JournalSkeleton, AnalyticsSkeleton } from "@/components/shared/PageSkeleton";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { KeyboardShortcutsModal } from "@/components/shared/KeyboardShortcutsModal";
import { TradeCSVImport } from "@/components/shared/TradeCSVImport";
import { PropChallengeMonitor } from "@/components/shared/PropChallengeMonitor";
import { OnboardingModal } from "@/components/onboarding/OnboardingModal";
import { OnboardingHints } from "@/components/onboarding/OnboardingHints";
import { useOnboarding } from "@/hooks/useOnboarding";

// Eager-loaded (critical path)
import Dashboard from "./pages/Dashboard";
import AuthPage from "./pages/AuthPage";
import NotFound from "./pages/NotFound";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";

// Landing page (only for logged-out users)
const LandingPage = lazy(() => import("./pages/Landing"));

// Lazy-loaded (non-critical pages)
const JournalPage = lazy(() => import("./pages/JournalPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const EdgeScorePage = lazy(() => import("./pages/EdgeScorePage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const PropGuardPage = lazy(() => import("./pages/PropGuardPage"));
const CommandCenterPage = lazy(() => import("./pages/CommandCenterPage"));
const TradeReplayPage = lazy(() => import("./pages/TradeReplayPage"));
const ExecutionModePage = lazy(() => import("./pages/ExecutionModePage"));
const ShopPage = lazy(() => import("./pages/ShopPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AgentsPage = lazy(() => import("./pages/AgentsPage"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage"));
const TraderProfilePage = lazy(() => import("./pages/TraderProfilePage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const TradingAIChatPage = lazy(() => import("./pages/TradingAIChatPage"));
const SessionDebriefPage = lazy(() => import("./pages/SessionDebriefPage"));
const AuthCallbackPage = lazy(() => import("./pages/AuthCallbackPage"));
const TradovateCallbackPage = lazy(() => import("./pages/TradovateCallbackPage"));
const WelcomePage = lazy(() => import("./pages/WelcomePage"));
const TaxPage = lazy(() => import("./pages/TaxPage"));
const FirmAdminPage = lazy(() => import("./pages/FirmAdminPage"));
const PropChallengePage = lazy(() => import("./pages/PropChallengePage"));

const queryClient = new QueryClient();

function RouteTransition({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <div key={location.pathname} className="page-enter">
      {children}
    </div>
  );
}

function PageFallback({ skeleton }: { skeleton?: React.ReactNode }) {
  return (
    <DashboardLayout>
      {skeleton || (
        <div className="space-y-4 animate-fade-in">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      )}
    </DashboardLayout>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { accepted, loading: legalLoading, accept } = useLegalAcceptance();
  const { onboardingNeeded, hintsNeeded, loading: onboardingLoading, refetch, dismissHints } = useOnboarding(user?.id ?? null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!loading && !legalLoading) return;
    const t = setTimeout(() => setTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, [loading, legalLoading]);

  if ((loading || legalLoading) && !timedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  // Timed out or loaded — force through
  if (!user) return <Navigate to="/auth" replace />;
  if (accepted === false) {
    return <LegalAcceptanceModal onAccept={accept} />;
  }
  return (
    <>
      {children}
      {!onboardingLoading && onboardingNeeded && (
        <OnboardingModal userId={user.id} onComplete={refetch} />
      )}
      {!onboardingLoading && hintsNeeded && (
        <OnboardingHints userId={user.id} onAllDismissed={dismissHints} />
      )}
    </>
  );
}

// Root: landing for logged-out, dashboard for logged-in
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
    </div>
  );
  if (!user) return (
    <Suspense fallback={<div className="min-h-screen bg-black" />}>
      <LandingPage />
    </Suspense>
  );
  return <ProtectedRoute><Dashboard /></ProtectedRoute>;
}

function AuthSync() {
  const { user } = useAuth();
  useEffect(() => { initAnalytics(); }, []); // load ad pixels once (no-op unless VITE_ pixel IDs set)
  useEffect(() => {
    setCurrentUserId(user?.id ?? null);
    if (user) {
      syncTradesFromDB();
      syncViolationsFromDB();
    }
  }, [user?.id]);
  return null;
}

const AGENT_NAMES: Record<string, string> = {
  journal: "Journal Agent",
  news: "News Agent",
  calendar: "Calendar Agent",
  risk: "Risk Agent",
  coach: "Coach Agent",
  __team__: "All Agents",
};

function AgentChatMonitor() {
  const { user } = useAuth();
  const location = useLocation();
  const locationRef = useRef(location.pathname);

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("agent-chat-monitor")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "agent_chat_history", filter: `user_id=eq.${user.id}` },
        (payload) => {
          if (!locationRef.current.startsWith("/agents")) {
            const agentId: string = (payload.new as any).agent_name ?? "unknown";
            agentChatService.incrementUnread(agentId, AGENT_NAMES[agentId] ?? "Agent");
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  return null;
}

// Capture ?ref=CODE from any landing URL and persist it for use on signup
function ReferralCapture() {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) {
      localStorage.setItem("mm-referral-code", ref.toUpperCase().trim());
    }
  }, [location.search]);
  return null;
}

// Apply saved theme on startup — dark terminal only, no light mode
(() => {
  const root = document.documentElement;
  root.classList.remove("dark", "light");
  root.classList.add("dark");
  localStorage.removeItem("mm-theme"); // clean up any stale light/system values
  const colorTheme = localStorage.getItem("mm-color-theme") || "chrome";
  root.setAttribute("data-theme", colorTheme);
})();

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TenantProvider>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <KeyboardShortcutsModal />
        <BrowserRouter>
          <CookieConsentBanner />
          <AuthSync />
          <AgentChatMonitor />
          <ReferralCapture />
          <PropChallengeMonitor />
          <TradeCSVImport />
          <RouteTransition>
          <Routes>
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth/callback" element={<Suspense fallback={<div />}><AuthCallbackPage /></Suspense>} />
            <Route path="/auth/v1/callback" element={<Suspense fallback={<div />}><AuthCallbackPage /></Suspense>} />
            <Route path="/auth/tradovate/callback" element={<Suspense fallback={<div />}><TradovateCallbackPage /></Suspense>} />
            <Route path="/welcome" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><WelcomePage /></Suspense>} />
            <Route path="/" element={<RootRoute />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/command-center" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><CommandCenterPage /></Suspense></ProtectedRoute>} />
            <Route path="/journal" element={<ProtectedRoute><Suspense fallback={<PageFallback skeleton={<JournalSkeleton />} />}><JournalPage /></Suspense></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><Suspense fallback={<PageFallback skeleton={<AnalyticsSkeleton />} />}><AnalyticsPage /></Suspense></ProtectedRoute>} />
            {/*  Active routes  */}
            <Route path="/prop-guard" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><PropGuardPage /></Suspense></ProtectedRoute>} />
            <Route path="/edge-score" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><EdgeScorePage /></Suspense></ProtectedRoute>} />
            <Route path="/prop-challenge" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><PropChallengePage /></Suspense></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><SettingsPage /></Suspense></ProtectedRoute>} />
            <Route path="/trade-replay" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><TradeReplayPage /></Suspense></ProtectedRoute>} />
            <Route path="/execution-terminal" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><ExecutionModePage /></Suspense></ProtectedRoute>} />
            <Route path="/execution" element={<ProtectedRoute><Navigate to="/execution-terminal" replace /></ProtectedRoute>} />
            <Route path="/shop" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><ShopPage /></Suspense></ProtectedRoute>} />
            {/* Marketplace / Strategy Library temporarily hidden for launch (see LEGAL_AUDIT.md) — redirect to billing. Restore <ShopPage/> + MARKETPLACE_ENABLED to bring back. */}
            <Route path="/marketplace" element={<ProtectedRoute><Navigate to="/shop" replace /></ProtectedRoute>} />
            <Route path="/strategy-library" element={<ProtectedRoute><Navigate to="/shop" replace /></ProtectedRoute>} />
            {/* Trading AI merged into Agents  redirect */}
            <Route path="/trading-ai" element={<ProtectedRoute><Navigate to="/agents" replace /></ProtectedRoute>} />
            <Route path="/trading-enquiries" element={<ProtectedRoute><Navigate to="/agents" replace /></ProtectedRoute>} />

            {/*  Consolidated routes — redirect to new homes  */}
            {/* Screenshots removed from nav — redirect to Settings */}
            <Route path="/screenshots" element={<ProtectedRoute><Navigate to="/settings" replace /></ProtectedRoute>} />
            {/* Risk Calculator moved to Settings  Risk tab */}
            <Route path="/risk-calc" element={<ProtectedRoute><Navigate to="/settings" replace /></ProtectedRoute>} />
            {/* Session Heatmap moved to Analytics */}
            <Route path="/sessions" element={<ProtectedRoute><Navigate to="/analytics" replace /></ProtectedRoute>} />
            {/* Violations merged into Prop Firm Guard */}
            <Route path="/violations" element={<ProtectedRoute><Navigate to="/prop-guard" replace /></ProtectedRoute>} />
            {/* Broker Hub moved to Settings  Connections */}
            <Route path="/broker-hub" element={<ProtectedRoute><Navigate to="/settings" replace /></ProtectedRoute>} />
            {/* Agent Hub removed — the network control-room view was cut; agents live at /agents */}
            <Route path="/agent-hub" element={<ProtectedRoute><Navigate to="/agents?agent=journal" replace /></ProtectedRoute>} />
            <Route path="/agents" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><AgentsPage /></Suspense></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><LeaderboardPage /></Suspense></ProtectedRoute>} />
            <Route path="/trader/:username" element={<Suspense fallback={<PageFallback />}><TraderProfilePage /></Suspense>} />
            <Route path="/admin" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><AdminPage /></Suspense></ProtectedRoute>} />
            <Route path="/trading-ai" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><TradingAIChatPage /></Suspense></ProtectedRoute>} />
            <Route path="/session-debrief" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><SessionDebriefPage /></Suspense></ProtectedRoute>} />
            <Route path="/tax" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><TaxPage /></Suspense></ProtectedRoute>} />
            <Route path="/firm-admin" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><FirmAdminPage /></Suspense></ProtectedRoute>} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/unsubscribe" element={<Suspense fallback={<div />}><UnsubscribePage /></Suspense>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </RouteTransition>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
    </TenantProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
